/**
 * Deck-profile layout viability helpers — shared between the web caster UI
 * (DeckProfileLayoutSection) and the overlay (DeckCardGrid).
 *
 * Single source of truth for:
 *   1. The grid layout constants used by DeckCardGrid to size cards.
 *   2. The fixed 1920×1080 overlay dimensions for the side-by-side deck-profile layout.
 *   3. `isProfileColumnViable` — whether a given column count fills the column width
 *      (width-bound) rather than shrinking to fit the height (height-bound).
 *   4. `computeSideboardColumnH` — proportional sideboard height in the left column.
 *
 * NOTE: DeckCardGrid.tsx in packages/overlay declares the same constants locally.
 * The overlay package should import from here; the local copies in DeckCardGrid.tsx
 * are the authoritative source that was used to derive these values.
 */
// ─── Grid layout constants (match DeckCardGrid.tsx exactly) ──────────────────
/** Portrait card aspect ratio: width / height ≈ 390÷546 ≈ 0.714. */
export const DECK_GRID_CARD_ASPECT = 0.714;
/** Gap between cards in the CSS grid (column-gap and row-gap). */
export const DECK_GRID_CARD_GAP = 4; // px
/** Gap between zone sections in the outer flex column. */
export const DECK_GRID_ZONE_GAP = 10; // px
/** Gap between the vertical zone-label column and the card grid in each zone row. */
export const DECK_GRID_ZONE_ROW_GAP = 4; // px
/** Width of the per-zone vertical label column on the left of each zone row. */
export const DECK_GRID_ZONE_LABEL_COL_W = 40; // px
// ─── 1920×1080 deck-profile overlay layout dimensions (side-by-side mode) ────
//
// Derived from DeckProfileOverlay.tsx layout chain:
//
//   Root:              1920 × 1080
//   Glass card:        inset 40 on all sides → 1840 × 1000 px
//   Top strip:         height 100, flexShrink 0 → remaining 900 px for main content
//   Bottom accent bar: absolute, 4 px — does NOT consume flex space
//   Main content:      flex 1, padding '10px 40px'
//                      → inner width  = 1840 − 40 − 40 = 1760 px
//                      → inner height = 900 − 10 − 10  =  880 px
//
//   Rail + card-grid flex row, gap 16:
//     Rail (left):     flexBasis '25%' of 1760 = 440 px
//     Card-grid area:  1760 − 440 − 16 = 1304 px wide, 880 px tall
//
//   Side-by-side within card-grid (1304 px, gap 16):
//     Left column:     flex '0 0 38%' → 1304 × 0.38 = 495.52 → rendered ≈ 496 px
//     Right column:    flex 1         → 1304 − 16 − 496 = 792 px
//
//   (Both columns share the full 880 px height before any internal proportional split.)
/**
 * Outer width of the main-deck (right) column in the 1920×1080 side-by-side layout.
 * DeckCardGrid.outerRef.clientWidth would measure this value.
 */
export const PROFILE_MAIN_COLUMN_W = 792; // px
/** Available height of the main-deck column. */
export const PROFILE_MAIN_COLUMN_H = 880; // px
/**
 * Outer width of the left column (material/loadout + sideboard) in the
 * 1920×1080 side-by-side layout.
 * DeckCardGrid.outerRef.clientWidth would measure this value for sideboard grid.
 */
export const PROFILE_SIDEBOARD_COLUMN_W = 496; // px (same physical column as material)
/**
 * Total height of the left column before it is proportionally divided between
 * the material section (top), the 1 px divider with 5 px margins (11 px total),
 * and the sideboard section (bottom). Used by computeSideboardColumnH.
 */
export const PROFILE_LEFT_COLUMN_H = 880; // px
// ─── Internal constant ───────────────────────────────────────────────────────
/**
 * Vertical space consumed by the thin divider between the material and sideboard
 * sections in the left column: 1 px rule + 5 px top margin + 5 px bottom margin.
 * Matches `margin: '5px 0'` on the divider div in DeckProfileOverlay.tsx.
 */
const PROFILE_LEFT_DIVIDER_H = 11; // px (1 + 5 + 5)
// ─── Helpers ─────────────────────────────────────────────────────────────────
/**
 * Slack allowed before a column count is treated as non-viable (grayed out).
 * A count is "viable enough" when its content height is within this multiple of
 * the available height — i.e. the overlay would only have to shrink each card to
 * ~1/tolerance of full-fill size, a mild reduction that still reads well on stream.
 * 1.2 ⇒ cards may shrink to ~83% before we gray the option; below that they look
 * noticeably small / leave a lot of empty width, which is what we want to flag.
 */
export const PROFILE_VIABILITY_TOLERANCE = 1.2;
/**
 * Returns true when `cardCount` distinct cards rendered at `perRow` columns
 * are **width-bound** (the card fills the full column width) rather than
 * **height-bound** (the overlay shrinks the card to fit available height).
 *
 * A column count is VIABLE when the content height at full card width ≤ H:
 *   W       = columnWidthPx − DECK_GRID_ZONE_LABEL_COL_W − DECK_GRID_ZONE_ROW_GAP
 *   cardW   = (W − (perRow−1) × DECK_GRID_CARD_GAP) / perRow
 *   cardH   = cardW / DECK_GRID_CARD_ASPECT
 *   rows    = ceil(cardCount / perRow)
 *   content = rows × cardH + max(0, rows−1) × DECK_GRID_CARD_GAP   [single zone]
 *   viable  = content ≤ columnHeightPx
 *
 * When the deck has no cards (cardCount ≤ 0) all column counts are considered
 * viable so the UI leaves all buttons enabled.
 *
 * Equivalent to the width-bound condition inside DeckCardGrid.tsx's fixedPerRow path:
 *   cwByWidth <= chByHeight × CARD_ASPECT
 * Both formulations are mathematically identical for a single zone.
 *
 * @param cardCount       Number of distinct card entries in the zone.
 * @param columnWidthPx   Outer component width of the DeckCardGrid container (px).
 *                        The label-column and gap are subtracted internally.
 * @param columnHeightPx  Available container height for the DeckCardGrid (px).
 * @param perRow          Column count to evaluate.
 */
export function isProfileColumnViable(cardCount, columnWidthPx, columnHeightPx, perRow, tolerance = PROFILE_VIABILITY_TOLERANCE) {
    // Empty deck or invalid perRow → all options viable (leave buttons enabled).
    if (cardCount <= 0 || perRow <= 0)
        return true;
    // Card-area width: strip the fixed zone-label column and the gap to the card grid.
    const W = columnWidthPx - DECK_GRID_ZONE_LABEL_COL_W - DECK_GRID_ZONE_ROW_GAP;
    const cardW = (W - (perRow - 1) * DECK_GRID_CARD_GAP) / perRow;
    const cardH = cardW / DECK_GRID_CARD_ASPECT;
    const rows = Math.ceil(cardCount / perRow);
    // Single zone: sum of row heights + inter-row gaps (no inter-zone ZONE_GAP).
    const contentH = rows * cardH + Math.max(0, rows - 1) * DECK_GRID_CARD_GAP;
    // Allow a little overflow (cards shrink mildly and still read well) before graying.
    return contentH <= columnHeightPx * tolerance;
}
/**
 * Computes the available height for the sideboard DeckCardGrid in the left column
 * of the 1920×1080 side-by-side deck-profile layout.
 *
 * The left column flexes its height proportionally between the material/loadout
 * section (top) and the sideboard section (bottom), matching the overlay's:
 *   flex: materialRows  (material div)
 *   flex: sideboardRows (sideboard div)
 * with a 1 px divider + 5 px margins (11 px total) between them.
 *
 * Approximation: uses the proportional flex formula directly. The actual pixel
 * height may differ by 1–2 px depending on the browser's subpixel rounding of
 * flex-grow allocation, but the error is below the threshold that would flip a
 * borderline viability decision.
 *
 * Material is always rendered at 4 columns (fixedPerRow={4} in DeckProfileOverlay.tsx),
 * so materialRows = max(1, ceil(materialCount / 4)) matches the overlay exactly.
 *
 * When sideboardCount ≤ 0 the sideboard section is hidden, so the function returns
 * PROFILE_LEFT_COLUMN_H (full height) — isProfileColumnViable will return true from
 * its own cardCount ≤ 0 guard in that case.
 *
 * @param materialCount   Distinct card entries in the material/loadout/extra zones combined.
 * @param sideboardCount  Distinct card entries in the sideboard zone.
 * @param sideboardPerRow Column count being evaluated for the sideboard.
 */
/**
 * Natural rendered height (px) of a single-zone card grid at a fixed column count,
 * sized so each row of `perRow` cards fills `columnWidthPx`. Used to size the
 * side-by-side left column's sections (material/loadout, sideboard) to their content
 * — so a sparse section doesn't leave dead space before the divider.
 *
 * Returns 0 for an empty zone.
 */
export function naturalGridHeight(cardCount, perRow, columnWidthPx) {
    if (cardCount <= 0 || perRow <= 0)
        return 0;
    const W = columnWidthPx - DECK_GRID_ZONE_LABEL_COL_W - DECK_GRID_ZONE_ROW_GAP;
    const cardW = (W - (perRow - 1) * DECK_GRID_CARD_GAP) / perRow;
    const cardH = cardW / DECK_GRID_CARD_ASPECT;
    const rows = Math.ceil(cardCount / perRow);
    return rows * cardH + Math.max(0, rows - 1) * DECK_GRID_CARD_GAP;
}
export function computeSideboardColumnH(materialCount, sideboardCount, sideboardPerRow) {
    if (sideboardCount <= 0)
        return PROFILE_LEFT_COLUMN_H;
    // Material is always hard-locked at 4 columns in DeckProfileOverlay.tsx.
    const materialRows = Math.max(1, Math.ceil(materialCount / 4));
    const sideboardRows = Math.max(1, Math.ceil(sideboardCount / Math.max(1, sideboardPerRow)));
    const totalRows = materialRows + sideboardRows;
    return (PROFILE_LEFT_COLUMN_H - PROFILE_LEFT_DIVIDER_H) * (sideboardRows / totalRows);
}
//# sourceMappingURL=deckProfileLayout.js.map